import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Mail, Phone, CheckCircle2 } from "lucide-react";

const PANEL = { hidden: { opacity: 0, y: 16, scale: 0.96 }, shown: { opacity: 1, y: 0, scale: 1 }, transition: { duration: 0.2 } };

function Avatar({ advisor, size = "h-10 w-10" }) {
  if (advisor?.photo_url) {
    return <img src={advisor.photo_url} alt={advisor.name} data-testid="advisor-photo" className={`${size} rounded-full object-cover ring-2 ring-white shadow`} />;
  }
  return (
    <div data-testid="advisor-photo-placeholder" className={`${size} rounded-full bg-coral text-white grid place-items-center font-bold ring-2 ring-white shadow`}>
      {(advisor?.name || "J").charAt(0)}
    </div>
  );
}

export function openAskJane(question = "") {
  window.dispatchEvent(new CustomEvent("kohc:ask-jane", { detail: { question } }));
}

function AskForm({ advisor, onSent, initialQuestion = "" }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", question: initialQuestion });
  useEffect(() => { if (initialQuestion) setForm((f) => ({ ...f, question: initialQuestion })); }, [initialQuestion]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/advisor/ask", { ...form, page: pathname });
      onSent();
    } catch (err) {
      toast.error(apiErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Your name</Label><Input data-testid="advisor-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label className="text-xs">Email</Label><Input data-testid="advisor-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
      </div>
      <div>
        <Label className="text-xs">Your question</Label>
        <Textarea data-testid="advisor-question-input" required minLength={5} rows={4} placeholder="e.g. How often should dentures be cleaned?" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
      </div>
      <Button data-testid="advisor-submit-btn" type="submit" disabled={busy} className="w-full rounded-full bg-coral hover:bg-coral/90 text-white">
        <Send className="h-4 w-4 mr-2" />{busy ? "Sending…" : `Send to ${advisor.name}`}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center">Or reach {advisor.name} directly:{" "}
        <a data-testid="advisor-email-link" href={`mailto:${advisor.email}`} className="underline inline-flex items-center gap-1"><Mail className="h-3 w-3" />{advisor.email}</a>{" · "}
        <a data-testid="advisor-phone-link" href={`tel:${(advisor.phone || "").replace(/\s/g, "")}`} className="underline inline-flex items-center gap-1"><Phone className="h-3 w-3" />{advisor.phone}</a>
      </p>
    </form>
  );
}

export default function AdvisorWidget() {
  const { pathname: path } = useLocation();
  const [advisor, setAdvisor] = useState(null);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [prefill, setPrefill] = useState("");

  useEffect(() => { api.get("/advisor").then((r) => setAdvisor(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { setPrefill(e.detail?.question || ""); setSent(false); setOpen(true); };
    window.addEventListener("kohc:ask-jane", h);
    return () => window.removeEventListener("kohc:ask-jane", h);
  }, []);
  if (!advisor || path.startsWith("/inspector/")) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3" data-testid="advisor-widget">
      <AnimatePresence>
        {open && (
          <motion.div initial={PANEL.hidden} animate={PANEL.shown} exit={PANEL.hidden} transition={PANEL.transition}
            data-testid="advisor-panel" className="w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white shadow-2xl border border-black/5 overflow-hidden">
            <div className="bg-brand text-white p-4 flex items-center gap-3">
              <Avatar advisor={advisor} size="h-14 w-14" />
              <div className="flex-1 min-w-0">
                <p className="font-bold leading-tight" data-testid="advisor-name">Meet {advisor.name}</p>
                <p className="text-xs text-white/80" data-testid="advisor-title">{advisor.title}</p>
              </div>
              <button data-testid="advisor-close-btn" onClick={() => setOpen(false)} aria-label="Close" className="p-1 rounded-full hover:bg-white/10"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4">
              {sent ? (
                <div className="text-center py-6" data-testid="advisor-sent">
                  <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold text-brand">Sent to {advisor.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">She'll reply to your email, usually within one working day.</p>
                  <Button variant="outline" size="sm" data-testid="advisor-ask-another-btn" className="mt-4 rounded-full" onClick={() => setSent(false)}>Ask another question</Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-3" data-testid="advisor-intro">{advisor.intro}</p>
                  <AskForm advisor={advisor} onSent={() => setSent(true)} initialQuestion={prefill} />
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button data-testid="advisor-tab-btn" onClick={() => setOpen((o) => !o)}
        className="group flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-full bg-brand text-white shadow-lg hover:shadow-xl transition-[transform,box-shadow] hover:-translate-y-0.5">
        <Avatar advisor={advisor} />
        <span className="text-left leading-tight">
          <span className="block text-sm font-semibold">Ask me</span>
          <span className="block text-[11px] text-white/80">{advisor.title}</span>
        </span>
        <MessageCircle className="h-4 w-4 text-coral" />
      </button>
    </div>
  );
}
